import {
  Flex,
  type ListRef,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  Extrapolation,
  withTiming,
} from 'react-native-reanimated';
import { ScrollContext } from '../context/ScrollContext';
import { AnimatedList } from './AnimatedList';
import { SectionContainer } from './SectionContainer';
import { CoverImages } from './CoverImages';
import { MarketInfoBanner } from './MarketInfoBanner';
import { GoodsInfo } from './GoodsInfo';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ExpectedPurchasePrice } from './ExpectedPurchasePrice';
import { GoodsPromotions } from './GoodsPromotions';
import { CouponListBanner } from './CouponListBanner';
import { DeliveryInfo } from './DeliveryInfo';
import { MarketInfo } from './MarketInfo';
import type { GoodsDetailContentKeys } from '../types/GoodsDetailContentKeys';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { CategoryInfo } from './CategoryInfo';
import { ReviewSummary } from './ReviewSummary';
import { GoodsCarousel } from './GoodsCarousel';
import { ContentTab } from './ContentTab';
import type { GoodsDetailTab } from '../types/GoodsDetailTab';
import { GoodsContent } from './GoodsContent';
import { FloatingButtons } from './FloatingButtons';
import { BottomDockedButton } from './BottomDocked';
import { AffiliateFloatingButton } from './AffiliateFloating/AffiliateFloatingButton';
import { useAffiliateFloating } from '../hooks/useAffiliateFloating';
import { useLogViewGoodsDetail } from '../hooks/useLogViewGoodsDetail';
import { useLogClickGoodsDetailTab } from '../hooks/useLogClickGoodsDetailTab';
import { LiveCampaignBanner } from './LiveCampaignBanner';
import { useRelatedLiveCampaign } from '../queries/useRelatedLiveCampaign';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useLogGoodsAnalyticsEvent } from '../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { Metadata } from 'shared/src/component';
import {
  getMetaDescription,
  getMetaKeywords,
  getMetaTitle,
  getProductMeta,
} from '../utils/goodsMetaData';
import { isIOS, isNotNil, isWeb } from 'shared/src/utils';
import { usePreloadQuery } from '../hooks/usePreloadQuery';

const BOTTOM_DOCKED_BUTTON_HEIGHT = 56;
const BOTTOM_DOCKED_BUTTON_PADDING = 12 * 2;
const LIVE_CAMPAIGN_BANNER_HEIGHT = 48;

const useLiveCampaignCGroupLogging = () => {
  const { data: campaign } = useRelatedLiveCampaign();
  const isCGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['C'],
  });
  const logEvent = useLogGoodsAnalyticsEvent();
  const hasLogged = useRef(false);

  useEffect(() => {
    if (!campaign || !isCGroup || hasLogged.current) return;
    hasLogged.current = true;

    logEvent({
      name: 'VIEW_SHOPLIVE_TARGET_GOODS_DETAIL',
      channel: ['braze'],
      analytics: campaign.logging.analytics,
    });
  }, [campaign, isCGroup, logEvent]);
};

export const GoodsDetailContents: React.FC = () => {
  const scrollY = useSharedValue(0);
  const tabScrollX = useSharedValue(0);
  const { width } = useScreenSize();
  const { bottom } = useSafeArea();
  const listRef = useRef<ListRef<GoodsDetailContentKeys> | null>(null);
  const tabLastScrollYValueRef = useRef<{
    [key in GoodsDetailTab]: number;
  }>({
    INFORMATION: 0,
    REVIEW: 0,
    SIZE: 0,
    INQUIRY: 0,
  });

  const [sectionHeight, setSectionHeight] = useState<{
    [key: string]: number;
  }>({});

  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: liveCampaign } = useRelatedLiveCampaign();
  const isBGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['B'],
  });
  const showLiveBanner = !!liveCampaign && isBGroup;

  const [tab, setTab] = useState<GoodsDetailTab>('INFORMATION');
  const logClickGoodsDetailTab = useLogClickGoodsDetailTab();

  useLiveCampaignCGroupLogging();
  usePreloadQuery();

  const scrollHandler = useAnimatedScrollHandler(event => {
    scrollY.value = event.contentOffset.y;
  });

  const stickyItemPositionY = useMemo(() => {
    return Object.keys(sectionHeight)
      .filter(key => key !== 'content' && key !== 'content-tab')
      .reduce((prev, cur) => prev + sectionHeight[cur], 0);
  }, [sectionHeight]);

  const componentList = useMemo<GoodsDetailContentKeys[]>(() => {
    const list: GoodsDetailContentKeys[] = [];
    list.push('cover');
    list.push('promotions');
    list.push('market');
    list.push('info');
    list.push('expected-purchase-price');
    list.push('coupons');
    list.push('delivery-info');
    // PDP 저성과 컴포넌트 제거에 따른 컴포넌트 제거
    // by 연준호 2026. 01. 19
    // list.push('market-info');
    if (goodsDetail?.component_visibility.review_summary) {
      list.push('review-summary');
    }

    // PDP 저성과 컴포넌트 제거에 따른 컴포넌트 제거
    // by 연준호 2026. 01. 12
    // if (goodsDetail?.component_visibility.category) {
    //   list.push('category-info');
    // }
    if (isNotNil(goodsBasic) && isNotNil(goodsDetail)) {
      list.push('carousel');
    }
    list.push('content-tab');

    if (isNotNil(goodsBasic) && isNotNil(goodsDetail)) {
      list.push('content');
    }

    return list;
  }, [goodsBasic, goodsDetail]);

  const marginBottom = useMemo(() => {
    const DOCKED_PRIMARY_BUTTON_HEIGHT =
      BOTTOM_DOCKED_BUTTON_HEIGHT + BOTTOM_DOCKED_BUTTON_PADDING;
    return bottom + DOCKED_PRIMARY_BUTTON_HEIGHT;
  }, [bottom]);

  const scrollToStickyView = useCallback(() => {
    // console.log('scrollToStickyView');
    // listRef.current?.scrollToItem({
    //   item: 'content-tab',
    //   animated: true,
    // });
    listRef.current?.scrollToOffset({
      offset: stickyItemPositionY,
      animated: true,
    });
  }, [stickyItemPositionY]);

  const scrollToOffset = useCallback((offset: number, animated: boolean) => {
    listRef.current?.scrollToOffset({
      offset,
      animated,
    });
  }, []);

  const changeContentTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      setTab(nextTab);
      const tabList: GoodsDetailTab[] = [
        'INFORMATION',
        'REVIEW',
        'SIZE',
        'INQUIRY',
      ];
      if (isIOS) {
        //ios의 경우 하단 탭 indicator의 이동을 직접 제어함.
        tabScrollX.value = withTiming(
          tabList.findIndex(tab => tab === nextTab),
          { duration: 200 },
        );
      }
    },
    [tabScrollX],
  );

  const onChangeTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      if (tab !== nextTab) {
        logClickGoodsDetailTab(nextTab);
        //마지막 스크롤 저장
        tabLastScrollYValueRef.current = {
          ...tabLastScrollYValueRef.current,
          [tab]: scrollY.value,
        };

        if (scrollY.value > stickyItemPositionY) {
          setTimeout(() => {
            scrollToOffset(
              Math.max(
                tabLastScrollYValueRef.current[nextTab],
                stickyItemPositionY,
              ),
              false,
            );
          }, 1);
        }
      }
      setTab(nextTab);
    },
    [
      tab,
      logClickGoodsDetailTab,
      scrollY.value,
      stickyItemPositionY,
      scrollToOffset,
    ],
  );

  useLogViewGoodsDetail();

  const { floatingText, showFloating, onViewFloating, onClickFloating } =
    useAffiliateFloating();

  const bannerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [0, LIVE_CAMPAIGN_BANNER_HEIGHT],
            [0, -LIVE_CAMPAIGN_BANNER_HEIGHT],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  return (
    <ScrollContext
      value={{
        listRef,
        scrollY,
        scrollToStickyView,
        changeContentTab,
      }}
    >
      {isNotNil(goodsBasic) && (
        <Metadata
          title={getMetaTitle(goodsBasic)}
          imgSrc={goodsBasic?.cover_images[0] ?? ''}
          path={`/goods/${goodsBasic.sno}`}
          description={getMetaDescription(goodsBasic)}
          keywords={getMetaKeywords(goodsBasic)}
          productMeta={getProductMeta(goodsBasic)}
          isCanonical
        />
      )}
      {showLiveBanner && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            },
            bannerAnimatedStyle,
          ]}
        >
          <LiveCampaignBanner />
        </Animated.View>
      )}
      <AnimatedList
        ref={listRef}
        {...(isWeb ? { nativeID: 'goods-detail-scroll' } : {})}
        style={{ flex: 1, marginBottom }}
        contentContainerStyle={
          showLiveBanner
            ? { paddingTop: LIVE_CAMPAIGN_BANNER_HEIGHT }
            : undefined
        }
        onScroll={scrollHandler}
        data={componentList}
        stickyHeaderIndices={[
          componentList.findIndex(key => key === 'content-tab'),
        ]}
        renderItem={({ item }) => {
          if (item === 'cover') {
            return (
              <SectionContainer
                height={width}
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <CoverImages />
              </SectionContainer>
            );
          }

          if (item === 'promotions') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <GoodsPromotions />
              </SectionContainer>
            );
          }

          if (item === 'market') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <MarketInfoBanner />
              </SectionContainer>
            );
          }

          if (item === 'info') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <GoodsInfo />
              </SectionContainer>
            );
          }

          if (item === 'expected-purchase-price') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <ExpectedPurchasePrice />
              </SectionContainer>
            );
          }

          if (item === 'coupons') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <CouponListBanner />
              </SectionContainer>
            );
          }

          if (item === 'delivery-info') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <DeliveryInfo />
              </SectionContainer>
            );
          }

          if (item === 'market-info') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <MarketInfo />
              </SectionContainer>
            );
          }
          if (item === 'review-summary') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <ReviewSummary />
              </SectionContainer>
            );
          }

          if (item === 'category-info') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <CategoryInfo />
              </SectionContainer>
            );
          }

          if (item === 'carousel') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <GoodsCarousel />
              </SectionContainer>
            );
          }

          if (item === 'content-tab') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <ContentTab
                  tabScrollX={tabScrollX}
                  tab={tab}
                  onChangeTab={onChangeTab}
                />
              </SectionContainer>
            );
          }

          if (item === 'content') {
            return (
              <SectionContainer
                onChangeHeight={(height: number) => {
                  setSectionHeight(prev => ({ ...prev, [item]: height }));
                }}
              >
                <GoodsContent
                  tabScrollX={tabScrollX}
                  tab={tab}
                  onChangeTab={onChangeTab}
                />
              </SectionContainer>
            );
          }

          return (
            <Flex height={300} backgroundColor={'white'}>
              <Typography>{item}</Typography>
            </Flex>
          );
        }}
      />
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        alignItems={'center'}
      >
        <Flex width={width}>
          <BottomDockedButton />
        </Flex>
      </Flex>
      <FloatingButtons
        bottomOffset={marginBottom}
        stickyItemPositionY={stickyItemPositionY}
      />
      {showFloating && (
        <AffiliateFloatingButton
          text={floatingText}
          marginBottom={marginBottom}
          onPress={onClickFloating}
          onView={onViewFloating}
        />
      )}
    </ScrollContext>
  );
};
